'use client'

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BookOpen, Trophy } from 'lucide-react'

import type { BookDetails, RouterOutputs } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import { CarouselItem, Skeleton } from '@ui/web/components'
import { CollectionIcon, PresentationIcon, QuizIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import { useBreakpoint } from '@helper/web/hooks'
import cn from '@config/tailwind/cn'

import type { CardAction } from '../components'
import type { LessonModuleModalData } from '../components/lesson-module-modal'
import {
  BookCollectionCard,
  BookCoverCard,
  BookDetailModal,
  BookThemeCard,
  ChallengeCard,
  ContentCarousel,
  ContentFilterPills,
  ContentSearch,
  ContinueLessonCard,
  ContinueReadingCard,
  LessonModuleCard,
  LessonModuleModal,
  LibrarySliderCard,
} from '../components'
import { useSharedTRPC } from '../context'

type ContinueReadingItem = RouterOutputs['library']['getContinueReading'][number]
type ContinueLessonItem = RouterOutputs['lesson']['getContinueLesson'][number]
type NewEnrollmentCourse = RouterOutputs['lesson']['getAllLearnEnrollment']['data']['courses'][number]
type NewEnrollmentChallenge = RouterOutputs['lesson']['getAllLearnEnrollment']['data']['challenges'][number]
type NewEnrollmentPractice = RouterOutputs['lesson']['getAllLearnEnrollment']['data']['practices'][number]
type PublicChallenge = RouterOutputs['lesson']['getActiveChallenges'][number]

type BookTheme = RouterOutputs['library']['getAllBookThemes'][number]
type LibrarySlider = RouterOutputs['library']['getLibrarySliders'][number]
type PopularCollection = RouterOutputs['library']['getPopularBookCollections']['items'][number]

type ActivePill = 'popular' | /* 'popular-month' | */ 'newest' | (string & {})

const STATIC_PILLS = [
  { label: 'Terbaru', value: 'newest' },
  { label: 'Populer', value: 'popular' },
  // { label: 'Populer bulan ini', value: 'popular-month' },
]

const OPEN_MODAL_ACTION = (onSelect: () => void): CardAction => ({
  type: 'callback',
  onClick: onSelect,
})

interface HomepageProps {
  type: 'web' | 'school'
  renderBookThemeAction: (theme: BookTheme) => CardAction
  renderLibrarySliderAction: (slider: LibrarySlider) => CardAction
  renderBookCollectionAction: (collection: PopularCollection) => CardAction
  renderBookModalAction: (book: BookDetails) => CardAction
  renderLessonModuleModalAction: (module: LessonModuleModalData) => CardAction
  onSearch: (value: string) => void
  renderCollectionLink?: RenderLink
  renderLoveBookLink?: RenderLink
  onBookTagClick?: (type: 'author' | 'publisher' | 'level' | 'theme', id: string) => void
  onModuleTagClick?: (type: 'grade' | 'curriculum' | 'contributor', id: string) => void
  renderContinueReadingAction?: (item: ContinueReadingItem) => CardAction
  renderContinueLessonAction?: (item: ContinueLessonItem) => CardAction
  userType?: 'child' | 'main'
  renderNewEnrollmentAction?: (enrollmentId: string) => CardAction
  renderPublicChallengeAction?: (challenge: PublicChallenge) => CardAction
  showLoveLessonModuleButton?: boolean
  className?: string
}

function SectionSkeleton({ showText = true }: { showText?: boolean }) {
  return (
    <div className='srd:flex srd:flex-col srd:gap-y-2'>
      {showText && <Skeleton className='srd:mx-4 srd:h-5 srd:w-28' />}
      <Skeleton className='srd:mx-4 srd:h-40 srd:rounded-xl' />
    </div>
  )
}

function SectionError() {
  return (
    <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:px-4 srd:text-error')}>
      Gagal memuat konten. Coba lagi nanti.
    </p>
  )
}

function mapToLessonModuleModalData(
  details: RouterOutputs['lesson']['getLessonModuleDetails'] & { type: 'lessonModule' },
): LessonModuleModalData {
  return {
    id: details.id,
    title: details.title,
    learningFocus: details.learningFocus,
    coverUrl: details.coverUrl,
    estimatedTimeMinutes: details.estimatedTimeMinutes,
    grade: details.grade,
    curricula: details.curricula,
    contributors: details.contributors,
    lessons: details.lessons,
  }
}

export function Homepage({
  type,
  renderBookThemeAction,
  renderLibrarySliderAction,
  renderBookCollectionAction,
  renderBookModalAction,
  renderLessonModuleModalAction,
  renderContinueReadingAction,
  renderContinueLessonAction,
  userType,
  renderNewEnrollmentAction,
  renderPublicChallengeAction,
  onSearch,
  renderCollectionLink,
  renderLoveBookLink,
  onBookTagClick,
  onModuleTagClick,
  showLoveLessonModuleButton,
  className,
}: HomepageProps) {
  const trpc = useSharedTRPC()
  const [search, setSearch] = useState('')
  const [activePill, setActivePill] = useState<ActivePill>('newest')
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null)
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null)

  const isMd = useBreakpoint('md')

  const {
    data: bookThemes,
    isLoading: bookThemesLoading,
    isError: bookThemesError,
  } = useQuery(trpc.library.getAllBookThemes.queryOptions())
  const {
    data: sliders,
    isLoading: slidersLoading,
    isError: slidersError,
  } = useQuery(trpc.library.getLibrarySliders.queryOptions())
  const { data: contentSections, isError: contentSectionsError } = useQuery(
    trpc.library.getContentSections.queryOptions(),
  )

  const isPopular = activePill === 'popular'
  // const isPopularMonth = activePill === 'popular-month'
  const isNewest = activePill === 'newest'
  const isContentSection = !isPopular && /* !isPopularMonth && */ !isNewest

  const {
    data: popularBooks,
    isLoading: popularBooksLoading,
    isError: popularBooksError,
  } = useQuery({
    ...trpc.library.getPopularBooks.queryOptions({}),
    enabled: isPopular,
  })
  const {
    data: popularCollections,
    isLoading: popularCollectionsLoading,
    isError: popularCollectionsError,
  } = useQuery({
    ...trpc.library.getPopularBookCollections.queryOptions({}),
    enabled: isPopular,
  })
  const {
    data: popularModulesNonInClass,
    isLoading: popularModulesNonInClassLoading,
    isError: popularModulesNonInClassError,
  } = useQuery({
    ...trpc.lesson.getPopularLessonModules.queryOptions({ excludeInClass: true }),
    enabled: isPopular,
  })
  const {
    data: popularModulesAll,
    isLoading: popularModulesAllLoading,
    isError: popularModulesAllError,
  } = useQuery({
    ...trpc.lesson.getPopularLessonModules.queryOptions({ excludeInClass: false }),
    enabled: isPopular,
  })

  const {
    data: newestBooks,
    isLoading: newestBooksLoading,
    isError: newestBooksError,
  } = useQuery({
    ...trpc.library.getNewestBooks.queryOptions({}),
    enabled: isNewest,
  })
  const {
    data: newestCollections,
    isLoading: newestCollectionsLoading,
    isError: newestCollectionsError,
  } = useQuery({
    ...trpc.library.getNewestBookCollections.queryOptions({}),
    enabled: isNewest,
  })
  const {
    data: newestModulesNonInClass,
    isLoading: newestModulesNonInClassLoading,
    isError: newestModulesNonInClassError,
  } = useQuery({
    ...trpc.lesson.getNewestLessonModules.queryOptions({ excludeInClass: true }),
    enabled: isNewest,
  })
  const {
    data: newestModulesAll,
    isLoading: newestModulesAllLoading,
    isError: newestModulesAllError,
  } = useQuery({
    ...trpc.lesson.getNewestLessonModules.queryOptions({ excludeInClass: false }),
    enabled: isNewest,
  })

  // const {
  //   data: popularMonthBooks,
  //   isLoading: popularMonthBooksLoading,
  //   isError: popularMonthBooksError,
  // } = useQuery({
  //   ...trpc.library.getPopularBooksThisMonth.queryOptions({}),
  //   enabled: isPopularMonth,
  // })
  // const {
  //   data: popularMonthCollections,
  //   isLoading: popularMonthCollectionsLoading,
  //   isError: popularMonthCollectionsError,
  // } = useQuery({
  //   ...trpc.library.getPopularBookCollectionsThisMonth.queryOptions({}),
  //   enabled: isPopularMonth,
  // })
  // const {
  //   data: popularMonthModulesNonInClass,
  //   isLoading: popularMonthModulesNonInClassLoading,
  //   isError: popularMonthModulesNonInClassError,
  // } = useQuery({
  //   ...trpc.lesson.getPopularLessonModulesThisMonth.queryOptions({ excludeInClass: true }),
  //   enabled: isPopularMonth,
  // })
  // const {
  //   data: popularMonthModulesAll,
  //   isLoading: popularMonthModulesAllLoading,
  //   isError: popularMonthModulesAllError,
  // } = useQuery({
  //   ...trpc.lesson.getPopularLessonModulesThisMonth.queryOptions({ excludeInClass: false }),
  //   enabled: isPopularMonth,
  // })

  const {
    data: contentSectionItems,
    isLoading: contentSectionItemsLoading,
    isError: contentSectionItemsError,
  } = useQuery({
    ...trpc.library.getContentSectionItems.queryOptions({ sectionId: activePill }),
    enabled: isContentSection,
  })

  const {
    data: bookDetails,
    isLoading: bookDetailsLoading,
    isError: bookDetailsError,
  } = useQuery({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ...trpc.library.getBookDetails.queryOptions({ id: selectedBookId! }),
    enabled: !!selectedBookId,
  })

  const { data: continueReading, isLoading: continueReadingLoading } = useQuery({
    ...trpc.library.getContinueReading.queryOptions(),
    enabled: !!renderContinueReadingAction,
  })
  const { data: continueLesson, isLoading: continueLessonLoading } = useQuery({
    ...trpc.lesson.getContinueLesson.queryOptions(),
    enabled: !!renderContinueLessonAction,
  })
  // Hide finished courses/challenges: drop items whose enrollment is COMPLETED or fully progressed.
  const activeContinueLesson = (continueLesson ?? []).filter((item) => {
    const enrollment = item.learnProgress?.learnEnrollment
    return enrollment?.status !== 'COMPLETED' && (enrollment?.progress ?? 0) < 100
  })

  const { data: newEnrollments, isLoading: newEnrollmentsLoading } = useQuery({
    ...trpc.lesson.getAllLearnEnrollment.queryOptions({ isOngoing: false, pageSize: 'all' }),
    enabled: userType === 'child' && !!renderNewEnrollmentAction,
  })

  const newEnrollmentChallenges: NewEnrollmentChallenge[] = newEnrollments?.data.challenges ?? []
  const newEnrollmentCourses: NewEnrollmentCourse[] = newEnrollments?.data.courses ?? []
  const newEnrollmentPractices: NewEnrollmentPractice[] = newEnrollments?.data.practices ?? []
  const hasNewEnrollments =
    newEnrollmentChallenges.length > 0 || newEnrollmentCourses.length > 0 || newEnrollmentPractices.length > 0
  const hasNewCourses = newEnrollmentCourses.length > 0

  const { data: activeChallenges, isLoading: activeChallengesLoading } = useQuery({
    ...trpc.lesson.getActiveChallenges.queryOptions(),
    enabled: !!renderPublicChallengeAction,
  })
  // A public challenge the child already joined shows up as a new enrollment or under Lanjut Belajar.
  // Adults never join one; they follow it on the leaderboard.
  const publicChallenges: PublicChallenge[] = (activeChallenges ?? []).filter(
    (challenge) => userType !== 'child' || !challenge.isEnrolled,
  )
  const challengeCardCount = newEnrollmentChallenges.length + publicChallenges.length

  const {
    data: moduleDetails,
    isLoading: moduleDetailsLoading,
    isError: moduleDetailsError,
  } = useQuery({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ...trpc.lesson.getLessonModuleDetails.queryOptions({ lessonModuleId: selectedModuleId! }),
    enabled: !!selectedModuleId,
  })

  const mappedModuleData =
    moduleDetails?.type === 'lessonModule' ? mapToLessonModuleModalData(moduleDetails) : undefined

  const pills = contentSectionsError
    ? STATIC_PILLS
    : [...STATIC_PILLS, ...(contentSections ?? []).map((section) => ({ label: section.title, value: section.id }))]

  // : isPopularMonth ? popularMonthBooks?.items
  const activeBooks = isPopular ? popularBooks?.items : isNewest ? newestBooks?.items : undefined
  // : isPopularMonth ? popularMonthBooksLoading
  const activeBooksLoading = isPopular ? popularBooksLoading : newestBooksLoading
  // : isPopularMonth ? popularMonthBooksError
  const activeBooksError = isPopular ? popularBooksError : newestBooksError

  // : isPopularMonth ? popularMonthCollections?.items
  const activeCollections = isPopular ? popularCollections?.items : isNewest ? newestCollections?.items : undefined
  // : isPopularMonth ? popularMonthCollectionsLoading
  const activeCollectionsLoading = isPopular ? popularCollectionsLoading : newestCollectionsLoading
  // : isPopularMonth ? popularMonthCollectionsError
  const activeCollectionsError = isPopular ? popularCollectionsError : newestCollectionsError

  // : isPopularMonth ? popularMonthModulesNonInClass?.items
  const activeModulesNonInClass = isPopular
    ? popularModulesNonInClass?.items
    : isNewest
      ? newestModulesNonInClass?.items
      : undefined
  // : isPopularMonth ? popularMonthModulesNonInClassLoading
  const activeModulesNonInClassLoading = isPopular ? popularModulesNonInClassLoading : newestModulesNonInClassLoading
  // : isPopularMonth ? popularMonthModulesNonInClassError
  const activeModulesNonInClassError = isPopular ? popularModulesNonInClassError : newestModulesNonInClassError

  // : isPopularMonth ? popularMonthModulesAll?.items
  const activeModulesAll = isPopular ? popularModulesAll?.items : isNewest ? newestModulesAll?.items : undefined
  // : isPopularMonth ? popularMonthModulesAllLoading
  const activeModulesAllLoading = isPopular ? popularModulesAllLoading : newestModulesAllLoading
  // : isPopularMonth ? popularMonthModulesAllError
  const activeModulesAllError = isPopular ? popularModulesAllError : newestModulesAllError

  return (
    <div className={cn('srd:flex srd:flex-col srd:gap-y-6', className)} data-type={type}>
      {(userType === 'child' && renderNewEnrollmentAction) || renderPublicChallengeAction ? (
        newEnrollmentsLoading || activeChallengesLoading ? (
          <SectionSkeleton />
        ) : challengeCardCount > 0 ? (
          <ContentCarousel>
            {userType === 'child' &&
              renderNewEnrollmentAction &&
              newEnrollmentChallenges.map((item) => (
                <CarouselItem key={item.id} className='srd:basis-full'>
                  <ChallengeCard
                    id={item.id}
                    title={item.challenge.title}
                    label='Tantangan spesial buat kamu!'
                    coverUrl={item.challenge.imgThumbnailFileUrl ?? ''}
                    startAt={item.challenge.startDate}
                    endAt={item.challenge.endDate}
                    action={renderNewEnrollmentAction(item.id)}
                    lineClamp={challengeCardCount > 1}
                  />
                </CarouselItem>
              ))}
            {renderPublicChallengeAction &&
              publicChallenges.map((challenge) => (
                <CarouselItem key={challenge.id} className='srd:basis-full'>
                  <ChallengeCard
                    id={challenge.id}
                    title={challenge.title}
                    label={userType === 'child' ? 'Tantangan spesial buat kamu!' : 'Tantangan yang sedang berlangsung'}
                    coverUrl={challenge.coverUrl ?? ''}
                    startAt={challenge.startDate}
                    endAt={challenge.endDate}
                    action={renderPublicChallengeAction(challenge)}
                    {...(userType !== 'child' && { ctaLabel: 'Lihat peringkat', ctaIcon: Trophy })}
                    lineClamp={challengeCardCount > 1}
                  />
                </CarouselItem>
              ))}
          </ContentCarousel>
        ) : null
      ) : null}

      {userType === 'child' && renderNewEnrollmentAction ? (
        newEnrollmentsLoading ? (
          <SectionSkeleton />
        ) : hasNewEnrollments && hasNewCourses ? (
          <div className='srd:flex srd:flex-col srd:gap-y-4'>
            <span className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-white srd:px-4')}>
              Ada tugas baru buat kamu
            </span>

            {newEnrollmentCourses.length > 0 && (
              <ContentCarousel>
                {newEnrollmentCourses.map((item) => (
                  <CarouselItem key={item.id} className='srd:basis-auto'>
                    <ContinueLessonCard
                      id={item.id}
                      title={item.course.title}
                      coverUrl={item.course.lessonModule.imgThumbnailFileUrl}
                      progress={0}
                      showProgress={false}
                      action={renderNewEnrollmentAction(item.id)}
                    />
                  </CarouselItem>
                ))}
              </ContentCarousel>
            )}
          </div>
        ) : null
      ) : null}

      {renderContinueReadingAction ? (
        continueReadingLoading ? (
          <SectionSkeleton />
        ) : continueReading && continueReading.length > 0 ? (
          <div className='srd:flex srd:flex-col srd:gap-y-2'>
            <span className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-white srd:px-4')}>
              Lanjut Baca
            </span>

            <ContentCarousel>
              {continueReading.map((item) => {
                const totalPages = item.book.page ?? 0
                const progress = totalPages > 0 ? (item.currentPage / totalPages) * 100 : 0
                return (
                  <CarouselItem key={item.id} className='srd:basis-auto'>
                    <ContinueReadingCard
                      id={item.bookId}
                      title={item.book.title}
                      coverUrl={item.book.coverUrl}
                      progress={progress}
                      action={renderContinueReadingAction(item)}
                    />
                  </CarouselItem>
                )
              })}
            </ContentCarousel>
          </div>
        ) : null
      ) : null}

      {renderContinueLessonAction ? (
        continueLessonLoading ? (
          <SectionSkeleton />
        ) : activeContinueLesson.length > 0 ? (
          <div className='srd:flex srd:flex-col srd:gap-y-2'>
            <span className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-white srd:px-4')}>
              Lanjut Belajar
            </span>

            <ContentCarousel>
              {activeContinueLesson.map((item) => (
                <CarouselItem key={item.id} className='srd:basis-auto'>
                  <ContinueLessonCard
                    id={item.id}
                    title={item.title}
                    coverUrl={item.coverUrl}
                    progress={item.learnProgress?.learnEnrollment?.progress ?? 0}
                    action={renderContinueLessonAction(item)}
                  />
                </CarouselItem>
              ))}
            </ContentCarousel>
          </div>
        ) : null
      ) : null}

      {bookThemesError ? (
        <SectionError />
      ) : bookThemesLoading ? (
        <SectionSkeleton showText={false} />
      ) : bookThemes && bookThemes.length > 0 ? (
        <ContentCarousel arrowSize='small'>
          {bookThemes.map((theme) => (
            <CarouselItem key={theme.id} className='srd:basis-auto'>
              <BookThemeCard
                id={theme.id}
                name={theme.name}
                iconUrl={theme.iconUrl}
                action={renderBookThemeAction(theme)}
              />
            </CarouselItem>
          ))}
        </ContentCarousel>
      ) : null}

      {slidersError ? (
        <SectionError />
      ) : slidersLoading ? (
        <SectionSkeleton />
      ) : sliders && sliders.length > 0 ? (
        <div className='srd:flex srd:flex-col srd:gap-y-2'>
          <span className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-white srd:px-4')}>
            Buat kamu
          </span>

          <ContentCarousel align='center' loop autoplay>
            {sliders.map((slider) => (
              <CarouselItem key={slider.id} className='srd:basis-auto'>
                <LibrarySliderCard
                  size={isMd ? 'large' : 'medium'}
                  id={slider.id}
                  imageUrl={slider.imageUrl}
                  action={renderLibrarySliderAction(slider)}
                />
              </CarouselItem>
            ))}
          </ContentCarousel>
        </div>
      ) : null}

      <div className='srd:flex srd:flex-wrap srd:gap-4 srd:items-center srd:px-4'>
        <ContentSearch
          value={search}
          onChange={setSearch}
          onSubmit={onSearch}
          className='srd:w-full srd:sm:max-w-md'
          variant='modal'
        />

        <ContentFilterPills
          activeFilter={activePill}
          filters={pills}
          onFilterChange={setActivePill}
          className='srd:flex-1 srd:min-w-sm'
        />
      </div>

      {isContentSection ? (
        contentSectionItemsError ? (
          <SectionError />
        ) : contentSectionItemsLoading ? (
          <>
            <SectionSkeleton />
            <SectionSkeleton />
            <SectionSkeleton />
            <SectionSkeleton />
          </>
        ) : contentSectionItems ? (
          contentSectionItems.books.length === 0 &&
          contentSectionItems.bookCollections.length === 0 &&
          contentSectionItems.lessonModulesPractice.length === 0 &&
          contentSectionItems.lessonModulesInClass.length === 0 ? (
            <p
              className={cn(
                typographyVariants({ variant: 'body-sm' }),
                'srd:px-4 srd:text-neutral-white srd:opacity-60',
              )}
            >
              Belum ada konten di seksi ini.
            </p>
          ) : (
            <>
              {contentSectionItems.books.length > 0 && (
                <div className='srd:flex srd:flex-col srd:gap-y-2'>
                  <div className='srd:flex srd:items-center srd:gap-x-2 srd:px-4'>
                    <BookOpen className='srd:size-6' color='var(--neutral-white)' />
                    <span className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-white')}>
                      Buku
                    </span>
                  </div>
                  <ContentCarousel loop>
                    {contentSectionItems.books.map((book) => (
                      <CarouselItem key={book.id} className='srd:basis-auto'>
                        <BookCoverCard
                          id={book.id}
                          coverUrl={book.coverUrl}
                          action={OPEN_MODAL_ACTION(() => setSelectedBookId(book.id))}
                        />
                      </CarouselItem>
                    ))}
                  </ContentCarousel>
                </div>
              )}
              {contentSectionItems.bookCollections.length > 0 && (
                <div className='srd:flex srd:flex-col srd:gap-y-2'>
                  <div className='srd:flex srd:items-center srd:gap-x-2 srd:px-4'>
                    <CollectionIcon className='srd:size-6' color='var(--neutral-white)' />
                    <span className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-white')}>
                      Koleksi
                    </span>
                  </div>
                  <ContentCarousel>
                    {contentSectionItems.bookCollections.map((col) => (
                      <CarouselItem key={col.id} className='srd:basis-auto'>
                        <BookCollectionCard
                          id={col.id}
                          coverUrl={col.coverUrl}
                          action={renderBookCollectionAction(col)}
                        />
                      </CarouselItem>
                    ))}
                  </ContentCarousel>
                </div>
              )}
              {contentSectionItems.lessonModulesPractice.length > 0 && (
                <div className='srd:flex srd:flex-col srd:gap-y-2'>
                  <div className='srd:flex srd:items-center srd:gap-x-2 srd:px-4'>
                    <QuizIcon className='srd:size-6' color='var(--neutral-white)' />
                    <span className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-white')}>
                      Latihan dan Kuis
                    </span>
                  </div>
                  <ContentCarousel>
                    {contentSectionItems.lessonModulesPractice.map((mod) => (
                      <CarouselItem key={mod.id} className='srd:basis-auto'>
                        <LessonModuleCard
                          id={mod.id}
                          coverUrl={mod.coverUrl}
                          action={OPEN_MODAL_ACTION(() => setSelectedModuleId(mod.id))}
                        />
                      </CarouselItem>
                    ))}
                  </ContentCarousel>
                </div>
              )}
              {contentSectionItems.lessonModulesInClass.length > 0 && (
                <div className='srd:flex srd:flex-col srd:gap-y-2'>
                  <div className='srd:flex srd:items-center srd:gap-x-2 srd:px-4'>
                    <PresentationIcon className='srd:size-6' color='var(--neutral-white)' />
                    <span className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-white')}>
                      Proyek dan Tugas
                    </span>
                  </div>
                  <ContentCarousel>
                    {contentSectionItems.lessonModulesInClass.map((mod) => (
                      <CarouselItem key={mod.id} className='srd:basis-auto'>
                        <LessonModuleCard
                          id={mod.id}
                          coverUrl={mod.coverUrl}
                          action={OPEN_MODAL_ACTION(() => setSelectedModuleId(mod.id))}
                        />
                      </CarouselItem>
                    ))}
                  </ContentCarousel>
                </div>
              )}
            </>
          )
        ) : null
      ) : (
        <>
          {activeBooksError ? (
            <SectionError />
          ) : activeBooksLoading ? (
            <SectionSkeleton />
          ) : activeBooks && activeBooks.length > 0 ? (
            <div className='srd:flex srd:flex-col srd:gap-y-2'>
              <div className='srd:flex srd:items-center srd:gap-x-2 srd:px-4'>
                <BookOpen className='srd:size-6' color='var(--neutral-white)' />
                <span className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-white')}>Buku</span>
              </div>
              <ContentCarousel>
                {activeBooks.map((book) => (
                  <CarouselItem key={book.id} className='srd:basis-auto'>
                    <BookCoverCard
                      id={book.id}
                      coverUrl={book.coverUrl}
                      action={OPEN_MODAL_ACTION(() => setSelectedBookId(book.id))}
                    />
                  </CarouselItem>
                ))}
              </ContentCarousel>
            </div>
          ) : null}

          {activeCollectionsError ? (
            <SectionError />
          ) : activeCollectionsLoading ? (
            <SectionSkeleton />
          ) : activeCollections && activeCollections.length > 0 ? (
            <div className='srd:flex srd:flex-col srd:gap-y-2'>
              <div className='srd:flex srd:items-center srd:gap-x-2 srd:px-4'>
                <CollectionIcon className='srd:size-6' color='var(--neutral-white)' />
                <span className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-white')}>
                  Seri dan Kompilasi
                </span>
              </div>
              <ContentCarousel>
                {activeCollections.map((col) => (
                  <CarouselItem key={col.id} className='srd:basis-auto'>
                    <BookCollectionCard id={col.id} coverUrl={col.coverUrl} action={renderBookCollectionAction(col)} />
                  </CarouselItem>
                ))}
              </ContentCarousel>
            </div>
          ) : null}

          {activeModulesNonInClassError ? (
            <SectionError />
          ) : activeModulesNonInClassLoading ? (
            <SectionSkeleton />
          ) : activeModulesNonInClass && activeModulesNonInClass.length > 0 ? (
            <div className='srd:flex srd:flex-col srd:gap-y-2'>
              <div className='srd:flex srd:items-center srd:gap-x-2 srd:px-4'>
                <QuizIcon className='srd:size-6' color='var(--neutral-white)' />
                <span className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-white')}>
                  Latihan dan Kuis
                </span>
              </div>
              <ContentCarousel>
                {activeModulesNonInClass.map((mod) => (
                  <CarouselItem key={mod.id} className='srd:basis-auto'>
                    <LessonModuleCard
                      id={mod.id}
                      coverUrl={mod.coverUrl}
                      action={OPEN_MODAL_ACTION(() => setSelectedModuleId(mod.id))}
                    />
                  </CarouselItem>
                ))}
              </ContentCarousel>
            </div>
          ) : null}

          {activeModulesAllError ? (
            <SectionError />
          ) : activeModulesAllLoading ? (
            <SectionSkeleton />
          ) : activeModulesAll && activeModulesAll.length > 0 ? (
            <div className='srd:flex srd:flex-col srd:gap-y-2'>
              <div className='srd:flex srd:items-center srd:gap-x-2 srd:px-4'>
                <PresentationIcon className='srd:size-6' color='var(--neutral-white)' />
                <span className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-white')}>
                  Proyek dan Tugas
                </span>
              </div>
              <ContentCarousel>
                {activeModulesAll.map((mod) => (
                  <CarouselItem key={mod.id} className='srd:basis-auto'>
                    <LessonModuleCard
                      id={mod.id}
                      coverUrl={mod.coverUrl}
                      action={OPEN_MODAL_ACTION(() => setSelectedModuleId(mod.id))}
                    />
                  </CarouselItem>
                ))}
              </ContentCarousel>
            </div>
          ) : null}
        </>
      )}

      <BookDetailModal
        open={!!selectedBookId}
        onClose={() => setSelectedBookId(null)}
        book={bookDetails}
        isLoading={bookDetailsLoading && !!selectedBookId}
        isError={bookDetailsError}
        renderBookAction={renderBookModalAction}
        renderCollectionLink={renderCollectionLink}
        renderLoveBookLink={renderLoveBookLink}
        onTagClick={onBookTagClick}
        withShareCta
      />

      <LessonModuleModal
        open={!!selectedModuleId}
        onClose={() => setSelectedModuleId(null)}
        data={mappedModuleData}
        isLoading={moduleDetailsLoading && !!selectedModuleId}
        isError={moduleDetailsError}
        renderActivityAction={mappedModuleData ? renderLessonModuleModalAction(mappedModuleData) : undefined}
        showLoveButton={showLoveLessonModuleButton}
        onTagClick={onModuleTagClick}
      />
    </div>
  )
}
